import React, { useEffect } from "react";
import {
  FaGraduationCap,
  FaChalkboardTeacher,
  FaUserCircle,
} from "react-icons/fa";
import PopularLearningDomains from "./LearningPage";
import SuccessStoriesShowcase from "./SuccessStories";
import ExperienceBanner from "./Banner";
import HomeExtraSections from "./ExtraSections";

type QuickCard = {
  icon: React.ReactNode;
  title: string;
  desc: string;
  link: string;
  keywords: string;
};

type StatItem = {
  number: string;
  label: string;
  seoLabel: string;
};

const Home: React.FC = () => {
  const goToLogin = () => {
    window.location.href = "/login";
  };

  useEffect(() => {
    const title =
      "Mediit Academy | Online Classes for Class 7-10, JEE, NEET, CBSE and NCERT";

    const description =
      "Mediit Academy is an online learning platform for Class 7 to 10 students, CBSE and NCERT preparation, JEE Foundation, NEET UG, live classes, mock tests, study notes, mentor support and student performance tracking.";

    const keywords = [
      "Mediit Academy",
      "online learning platform",
      "online classes for students",
      "Class 7 online classes",
      "Class 8 online tuition",
      "Class 9 Maths Science coaching",
      "Class 10 CBSE board preparation",
      "CBSE online classes",
      "NCERT online learning",
      "school foundation course",
      "JEE Foundation course",
      "JEE Main online coaching",
      "NEET UG online coaching",
      "Physics Chemistry Maths coaching",
      "Physics Chemistry Biology coaching",
      "live online classes",
      "online mock tests",
      "study notes",
      "doubt support",
      "student dashboard",
      "performance tracking",
      "mentor guidance",
      "online LMS platform",
    ].join(", ");

    document.title = title;

    const setMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    const setPropertyTag = (property: string, content: string) => {
      let tag = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMetaTag("description", description);
    setMetaTag("keywords", keywords);
    setMetaTag("robots", "index, follow");
    setMetaTag("author", "Mediit Academy");

    setPropertyTag("og:title", title);
    setPropertyTag("og:description", description);
    setPropertyTag("og:type", "website");
    setPropertyTag("og:site_name", "Mediit Academy");

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);

    let canonical = document.querySelector(
      `link[rel="canonical"]`
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", window.location.origin);
  }, []);

  const CourseIcon = () => (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
      <path d="M12 3L2 8L12 13L22 8L12 3Z" fill="#364052" />
      <path
        d="M5 10.5V15.5C5 16.4 8.2 19 12 19C15.8 19 19 16.4 19 15.5V10.5L12 14L5 10.5Z"
        fill="#364052"
      />
      <path d="M22 8V14" stroke="#364052" strokeWidth="1.8" />
    </svg>
  );

  const CommunityIcon = () => (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
      <circle cx="8" cy="8" r="3" fill="#364052" />
      <circle cx="16" cy="8" r="3" fill="#364052" />
      <path
        d="M3 19C3.4 15.8 5.4 14 8 14C10.6 14 12.6 15.8 13 19H3Z"
        fill="#364052"
      />
      <path
        d="M11 19C11.4 15.8 13.4 14 16 14C18.6 14 20.6 15.8 21 19H11Z"
        fill="#364052"
        opacity="0.9"
      />
    </svg>
  );

  const TestIcon = () => (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
      <path
        d="M6 3H14L19 8V21H6V3Z"
        fill="#364052"
        stroke="#364052"
        strokeWidth="1.5"
      />
      <path d="M14 3V8H19" fill="#fff" opacity="0.45" />
      <path
        d="M9 12H16"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M9 16H15"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );

  const BookIcon = () => (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
      <rect x="5" y="3" width="14" height="18" rx="2" fill="#364052" />
      <path
        d="M8 7H16"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M8 11H16"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M8 15H13"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );

  const MentorIcon = () => (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="5" width="16" height="15" rx="2" fill="#364052" />
      <path
        d="M8 3V7"
        stroke="#364052"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M16 3V7"
        stroke="#364052"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path d="M4 9H20" stroke="white" strokeWidth="1.5" />
      <path
        d="M8 13H10"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M13 13H16"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M8 17H15"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );

  const ServiceIcon = () => (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="3" width="14" height="18" rx="2" fill="#364052" />
      <path
        d="M8 7H14"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M8 11H14"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M8 15H12"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M15.5 17.5L20.5 12.5L22 14L17 19H15.5V17.5Z"
        fill="#364052"
        stroke="white"
        strokeWidth="0.8"
      />
    </svg>
  );

  const quickCards: QuickCard[] = [
    {
      icon: <CourseIcon />,
      title: "Courses",
      desc: "Explore online courses for Class 7 to 10, CBSE, NCERT, JEE Foundation and NEET preparation.",
      link: "/courses",
      keywords:
        "online courses, Class 7 courses, Class 8 courses, Class 9 courses, Class 10 courses, JEE Foundation, NEET preparation",
    },
    {
      icon: <CommunityIcon />,
      title: "Community",
      desc: "Join the student learning community for doubts, discussions, study groups and mentor guidance.",
      link: "/community",
      keywords:
        "student community, online study group, doubt discussion, JEE community, NEET community, CBSE learning community",
    },
    {
      icon: <TestIcon />,
      title: "Mock Tests",
      desc: "Practice with online mock tests, chapter-wise tests, score reports and exam-focused assessments.",
      link: "/real-test",
      keywords:
        "online mock tests, JEE mock tests, NEET mock tests, CBSE test series, chapter tests, score analysis",
    },
    {
      icon: <BookIcon />,
      title: "Study Notes",
      desc: "Access structured study notes, PDFs, revision material, assignments and subject-wise learning resources.",
      link: "/study-notes",
      keywords:
        "study notes, NCERT notes, Class 10 notes, JEE notes, NEET notes, revision notes, PDFs",
    },
    {
      icon: <MentorIcon />,
      title: "Mentors",
      desc: "Learn from academic mentors for Maths, Science, Physics, Chemistry, Biology, JEE and NEET.",
      link: "/our-mentors",
      keywords:
        "academic mentors, online teachers, JEE mentors, NEET mentors, Maths teacher, Science teacher, doubt mentors",
    },
    {
      icon: <ServiceIcon />,
      title: "Student Support",
      desc: "Get help for login, course access, live classes, payment, refund, test series and dashboard support.",
      link: "/student-support",
      keywords:
        "student support, login help, course access support, live class support, payment support, test series help",
    },
  ];

  const stats: StatItem[] = [
    {
      number: "150+",
      label: "Live Classes",
      seoLabel: "150+ live online classes for school, JEE and NEET students",
    },
    {
      number: "25k+",
      label: "Learners",
      seoLabel: "25k+ assisted learners through online learning support",
    },
    {
      number: "4.9/5",
      label: "Student Rating",
      seoLabel: "4.9 out of 5 student learning experience rating",
    },
    {
      number: "100%",
      label: "Guided Learning",
      seoLabel: "100% guided learning with courses, notes, tests and mentors",
    },
  ];

  const mentorImages = [
    {
      src: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&auto=format&q=80",
      alt: "Online academic mentor for live classes and student guidance",
    },
    {
      src: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&auto=format&q=80",
      alt: "Expert teacher for CBSE NCERT JEE and NEET preparation",
    },
    {
      src: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&auto=format&q=80",
      alt: "Subject mentor for Maths Science Physics Chemistry and Biology",
    },
    {
      src: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&auto=format&q=80",
      alt: "Student learning mentor for online doubt support",
    },
  ];

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    name: "Mediit Academy",
    description:
      "Mediit Academy is an online learning platform for Class 7 to 10 students, CBSE and NCERT learners, JEE Foundation students and NEET aspirants with live classes, study notes, mock tests, doubt support and performance tracking.",
    url: "/",
    slogan:
      "Online classes, mock tests, study notes and mentor support for school, JEE and NEET students.",
    keywords:
      "Mediit Academy, online classes, Class 7, Class 8, Class 9, Class 10, CBSE, NCERT, JEE Foundation, NEET UG, live classes, mock tests, study notes, doubt support",
    knowsAbout: [
      "Class 7 Online Classes",
      "Class 8 Online Tuition",
      "Class 9 Maths and Science",
      "Class 10 CBSE Board Preparation",
      "CBSE NCERT Learning",
      "JEE Foundation",
      "JEE Main Preparation",
      "NEET UG Preparation",
      "Online Mock Tests",
      "Study Notes",
      "Doubt Solving",
      "Student Dashboard",
      "Mentor Guidance",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Mediit Academy Learning Programs",
      itemListElement: quickCards.map((card, index) => ({
        "@type": "Offer",
        position: index + 1,
        itemOffered: {
          "@type": "Service",
          name: card.title,
          description: card.desc,
          url: card.link,
          keywords: card.keywords,
        },
      })),
    },
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Mediit Academy Home",
    description:
      "Home page of Mediit Academy with online courses, live batches, mock tests, study notes, community support and mentor guidance.",
    url: "/",
    mainEntity: {
      "@type": "ItemList",
      name: "Mediit Academy Quick Learning Links",
      itemListElement: quickCards.map((card, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: card.title,
        description: card.desc,
        url: card.link,
      })),
    },
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-white pb-28 font-['Inter'] text-[#101827] md:pb-0">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(homeSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(webPageSchema),
        }}
      />

      <main>
        {/* HERO SECTION */}
        <section
          className="relative z-20 overflow-visible bg-gradient-to-br from-[#f8fbff] via-[#f5f8fc] to-[#eef4fb]"
          aria-labelledby="home-hero-heading"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_84%_20%,rgba(255,255,255,0.98),transparent_36%),radial-gradient(circle_at_14%_20%,rgba(203,213,225,0.34),transparent_38%)]" />

          <div className="relative mx-auto max-w-[1460px] px-4 sm:px-6 lg:px-8 xl:px-8 2xl:px-3">
            <div className="grid grid-cols-1 items-center gap-10 py-10 sm:py-12 lg:grid-cols-12 lg:gap-8 lg:py-16 2xl:gap-14">
              {/* LEFT DARK CARD */}
              <div className="z-40 order-2 lg:order-1 lg:col-span-5 xl:ml-0 2xl:-ml-20">
                <div className="relative min-h-[285px] w-full overflow-hidden rounded-[30px] bg-gradient-to-br from-[#070d19] via-[#17172b] to-[#3a2417] p-6 shadow-2xl sm:p-7 lg:min-h-[300px] xl:w-full 2xl:w-[90%]">
                  <div className="absolute -right-20 -top-20 h-60 w-60 rounded-full bg-orange-500/20 blur-3xl" />
                  <div className="absolute -left-20 -bottom-20 h-60 w-60 rounded-full bg-blue-500/20 blur-3xl" />

                  <div className="relative z-10">
                    <div className="mb-6 flex flex-wrap items-center gap-4">
                      <div className="rounded-xl border border-white/15 bg-white/10 px-5 py-2 font-['Inter'] text-lg font-black tracking-wide text-white shadow-inner backdrop-blur-md sm:text-xl xl:text-2xl">
                        FOUNDATION BATCH 2.0
                      </div>

                      <div className="flex items-center gap-2 text-base font-semibold text-white">
                        <span className="h-3 w-3 rounded-full bg-green-400 shadow-[0_0_12px_rgba(74,222,128,0.9)]" />
                        Live Online Classes
                      </div>
                    </div>

                    <div className="space-y-3.5 text-[15px] text-white/90 sm:text-base">
                      {[
                        "Daily live classes with experienced subject mentors",
                        "Live doubt solving, study notes and recorded learning support",
                        "Weekly mock tests, performance reports and practice assignments",
                      ].map((item) => (
                        <div key={item} className="flex items-start gap-3">
                          <span className="mt-0.5 text-xl text-white">✓</span>
                          <p className="leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 flex flex-wrap items-center gap-4">
                      <div className="flex -space-x-3">
                        {mentorImages.map((item, index) => (
                          <img
                            key={item.src}
                            src={item.src}
                            alt={item.alt}
                            loading={index === 0 ? "eager" : "lazy"}
                            decoding="async"
                            className="h-10 w-10 rounded-full border-2 border-white object-cover shadow-md"
                          />
                        ))}
                      </div>

                      <p className="text-sm font-semibold text-white sm:text-[15px]">
                        Learn from trusted academic mentors
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT HERO CONTENT */}
              <div className="relative z-20 order-1 lg:order-2 lg:col-span-7">
                <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-[0.9fr_1.1fr] lg:gap-10 xl:gap-12">
                  {/* TEXT */}
                  <div className="w-full">
                    <p className="mb-3 flex items-center gap-2 text-sm leading-5 text-slate-900">
                      <span>✨</span>
                      Your learning journey starts with the right guidance.
                    </p>

                    <h1
                      id="home-hero-heading"
                      className="max-w-[520px] text-[31px] font-black leading-[1.12] tracking-[-0.025em] text-[#07111b] sm:text-[38px] lg:text-[42px] xl:text-[46px]"
                    >
                      Online Classes for Class 7-10, JEE Foundation and School
                      Preparation
                    </h1>

                    <p className="mt-3 max-w-[450px] text-sm font-normal leading-6 text-slate-600 sm:text-[15px]">
                      A structured learning program for school students designed
                      to build strong concepts in Maths, Science, CBSE, NCERT
                      and early competitive preparation through live classes,
                      expert guidance, study notes, practice assignments, doubt
                      support and regular mock tests.
                    </p>

                    <p className="sr-only">
                      Mediit Academy online classes, Class 7 online classes,
                      Class 8 online tuition, Class 9 Maths Science coaching,
                      Class 10 board exam preparation, CBSE NCERT course, JEE
                      Foundation course, NEET preparation support, live classes,
                      mock tests, study notes, doubt solving and student
                      dashboard.
                    </p>

                    <div className="mt-6 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={goToLogin}
                        aria-label="Enroll now in Mediit Academy online learning program"
                        className="rounded-xl bg-[#101827] px-8 py-3.5 text-sm font-bold text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:bg-slate-800"
                      >
                        Enroll Now
                      </button>

                      <a
                        href="/courses"
                        aria-label="Explore Mediit Academy online courses"
                        className="rounded-xl border border-slate-300 bg-white px-8 py-3.5 text-sm font-bold text-slate-950 transition-all duration-300 hover:-translate-y-1 hover:bg-slate-50"
                      >
                        Explore Courses
                      </a>
                    </div>
                  </div>

                  {/* BIG IMAGE */}
                  <div className="relative hidden h-[390px] w-full sm:block lg:h-[430px] xl:h-[455px]">
                    <img
                      src="https://www.tovutilms.com/hs-fs/hubfs/Website/Use%20Case%20Page%20Assets/engaged.png?width=1536&height=1024&name=engaged.png"
                      alt="Student learning online with live classes study notes mock tests and mentor support"
                      loading="eager"
                      decoding="async"
                      className="h-full w-full scale-[1.22] object-contain object-center lg:scale-[1.35] xl:scale-[1.45]"
                    />

                    <div className="absolute bottom-5 left-4 right-4 z-30 flex items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white/95 px-4 py-3 shadow-lg backdrop-blur-sm">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white">
                        🗓️
                      </div>

                      <div>
                        <p className="text-lg font-black leading-none">25k+</p>
                        <p className="mt-1 text-[11px] font-medium text-slate-500">
                          Assisted learners
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* LOWER WHITE CONTENT */}
        <section className="relative z-10 bg-white pt-10 font-['Inter'] lg:pt-14">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8 xl:px-4">
            <div className="relative z-10 rounded-[28px] border border-slate-100 bg-white shadow-sm">
              <div className="grid grid-cols-1 gap-0 lg:grid-cols-12">
                {/* LEFT QUICK CARDS */}
                <div className="p-6 sm:p-7 lg:col-span-4 lg:pr-8">
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    {quickCards.map((card) => (
                      <a
                        key={card.title}
                        href={card.link}
                        title={card.keywords}
                        aria-label={`${card.title}: ${card.desc}`}
                        className="group flex min-h-[112px] flex-col items-center justify-center rounded-xl border border-slate-200 bg-white p-3 text-center transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-lg"
                      >
                        <span className="flex h-10 w-10 items-center justify-center transition-transform duration-300 group-hover:scale-110">
                          {card.icon}
                        </span>

                        <span className="mt-2 text-sm font-semibold leading-tight text-slate-950">
                          {card.title}
                        </span>

                        <span className="sr-only">{card.desc}</span>
                      </a>
                    ))}
                  </div>
                </div>

                {/* CENTER TEXT */}
                <div className="border-y border-slate-100 p-6 sm:p-8 lg:col-span-4 lg:border-x lg:border-y-0 lg:px-9">
                  <h2 className="text-[28px] font-black leading-tight text-[#101827] sm:text-3xl">
                    Complete LMS Learning Platform for Students
                  </h2>

                  <p className="mt-4 text-base leading-relaxed text-slate-700">
                    Access structured courses, live online classes, recorded
                    learning support, study notes, assignments, online mock
                    tests, score reports and mentor guidance from one simple
                    student learning dashboard.
                  </p>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={goToLogin}
                      aria-label="Get started with Mediit Academy LMS learning platform"
                      className="rounded-lg bg-[#101827] px-6 py-3 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-1 hover:bg-slate-800"
                    >
                      Get Started Now
                    </button>

                    <a
                      href="/courses"
                      aria-label="Explore school JEE NEET and online test courses"
                      className="rounded-lg border border-slate-300 bg-white px-6 py-3 text-sm font-bold text-slate-950 transition-all hover:-translate-y-1 hover:bg-slate-50"
                    >
                      Explore Courses
                    </a>
                  </div>
                </div>

                {/* RIGHT TRUST */}
                <div className="p-6 sm:p-8 lg:col-span-4 lg:pl-9">
                  <h3 className="text-[28px] font-black leading-tight text-[#101827] sm:text-3xl">
                    Trusted Learning Support with Progress Tracking
                  </h3>

                  <p className="mt-4 text-base leading-relaxed text-slate-600">
                    Track your course progress, attend live sessions, revise
                    with notes, attempt regular tests and improve your score with
                    structured learning reports.
                  </p>

                  <div className="mt-8 flex items-center justify-center gap-10">
                    <div
                      className="relative flex h-28 w-28 items-center justify-center rounded-full"
                      aria-label="Performance tracking progress circle"
                    >
                      <div className="absolute inset-0 rounded-full border-[8px] border-slate-100" />
                      <div className="absolute inset-0 rotate-45 rounded-full border-[8px] border-b-blue-500 border-l-transparent border-r-indigo-600 border-t-orange-500" />
                    </div>

                    <div className="space-y-4">
                      <div className="rounded-2xl bg-slate-100 px-5 py-4">
                        <div className="h-2 w-24 rounded-full bg-slate-300" />
                        <div className="mt-3 h-2 w-16 rounded-full bg-slate-200" />
                      </div>

                      <button
                        type="button"
                        onClick={goToLogin}
                        aria-label="Open student learning dashboard"
                        className="rounded-xl bg-indigo-600 px-8 py-3 text-xl font-black tracking-widest text-white shadow-md transition-all hover:bg-indigo-700"
                      >
                        •••
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* DESKTOP STATS */}
        <section className="mt-8 hidden border-y border-slate-800/40 bg-gradient-to-r from-[#07111b] via-[#17172f] to-[#3b2418] font-['Inter'] md:block">
          <div className="mx-auto grid max-w-[1380px] grid-cols-4 divide-x divide-white/10 px-4 sm:px-6 lg:px-8 xl:px-4">
            {stats.map((item) => (
              <div key={item.label} className="py-5 text-center">
                <p className="text-3xl font-black leading-none text-white">
                  {item.number}
                </p>
                <p className="mt-1 text-sm font-medium text-white/70">
                  {item.label}
                </p>
                <p className="sr-only">{item.seoLabel}</p>
              </div>
            ))}
          </div>
        </section>

        <PopularLearningDomains />
        <SuccessStoriesShowcase />
        <ExperienceBanner />
        <HomeExtraSections />
      </main>

      {/* MOBILE STATS */}
      <div className="fixed bottom-[62px] left-0 right-0 z-40 grid grid-cols-4 border-t border-slate-100 bg-white py-2 shadow-[0_-8px_20px_rgba(15,23,42,0.06)] md:hidden">
        {stats.map((item) => (
          <div key={item.label} className="text-center">
            <p className="text-base font-black leading-none text-slate-950">
              {item.number}
            </p>
            <p className="mt-1 text-[10px] font-medium text-slate-500">
              {item.label}
            </p>
          </div>
        ))}
      </div>

      {/* MOBILE BOTTOM NAV */}
      <div className="fixed bottom-0 left-0 right-0 z-40 grid grid-cols-3 border-t border-slate-100 bg-white py-2 shadow-[0_-8px_20px_rgba(15,23,42,0.06)] md:hidden">
        <a
          href="/live-batches"
          aria-label="View live batches"
          className="flex flex-col items-center justify-center gap-1 text-orange-600"
        >
          <FaGraduationCap className="text-[22px]" />
          <span className="text-xs font-semibold">Batches</span>
        </a>

        <a
          href="/courses"
          aria-label="View online classroom courses"
          className="flex flex-col items-center justify-center gap-1 text-slate-800"
        >
          <FaChalkboardTeacher className="text-[22px]" />
          <span className="text-xs font-semibold">Classroom</span>
        </a>

        <button
          type="button"
          onClick={goToLogin}
          aria-label="Open student profile login"
          className="flex flex-col items-center justify-center gap-1 text-slate-800"
        >
          <FaUserCircle className="text-[22px]" />
          <span className="text-xs font-semibold">Profile</span>
        </button>
      </div>
    </div>
  );
};

export default Home;